import { useEffect, useState } from 'react';
import { repairAPI, vehicleAPI, garageAPI, driverAPI, referenceAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import RepairDetailsCard from '../components/RepairDetailsCard';

// letter type → garage specialty key
const LETTER_SPEC = { repair:'mechanical', service:'service', battery:'battery', tyre:'tyre' };
const gSpecs = (g) => { try { const a = JSON.parse(g.specialties || '[]'); return Array.isArray(a) ? a : []; } catch { return []; } };
import { PageHeader, Button, Table, StatusBadge, Modal, Input, Select, Loading, Alert } from '../components/ui';
import { formatRs } from '../utils/currency';

const URGENCIES  = ['low','medium','high','critical'];
const REP_TYPES  = ['mechanical','electrical','body','preventive','other'];
const urgencyColor = { low:'text-green-600', medium:'text-amber-600', high:'text-red-600', critical:'text-red-700 font-bold' };

// ── Print helper ───────────────────────────────────────────────────────────────
const printContent = (htmlArray) => {
  // Accept single html string or array of htmls (one per page)
  const pages = Array.isArray(htmlArray) ? htmlArray : [htmlArray];
  const pageBreak = '<div style="page-break-after:always;"></div>';
  const combined = pages.join(pageBreak);

  // Use hidden iframe to avoid popup blocker
  const existingFrame = document.getElementById('print-frame');
  if (existingFrame) existingFrame.remove();

  const iframe = document.createElement('iframe');
  iframe.id = 'print-frame';
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<html><head><title>Letter</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:13px;line-height:1.9;padding:50px;max-width:800px;margin:0 auto;}
      h3{margin:0 0 4px;}
      table{width:100%;border-collapse:collapse;}
      td{padding:4px 8px;vertical-align:top;}
      .header{border-bottom:2px solid #333;margin-bottom:24px;padding-bottom:12px;}
      .sign{margin-top:60px;}
      @media print { @page { margin: 2cm; } }
    </style>
    </head><body>${combined}</body></html>`);
  doc.close();

  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  }, 300);
};

// ── Letter generators ──────────────────────────────────────────────────────────
const letterHeader = (branch) => `
  <div class="header">
    <h2 style="margin:0;">${branch?.name || '[BRANCH NAME]'}</h2>
    <p style="margin:2px 0;color:#555;">${branch?.address || '[ADDRESS]'}</p>
    <p style="margin:2px 0;color:#555;">Tel: ${branch?.phone || ''} &nbsp;|&nbsp; Email: ${branch?.email || ''}</p>
  </div>`;

const buildQuotationHTML = (repair, garage, branch, date, extra, deadline) => `
  ${letterHeader(branch)}
  <p>Date: ${date} &nbsp;&nbsp;&nbsp; Ref: QR-${repair.id}-${new Date().getFullYear()}</p>
  <p><strong>To,</strong><br/>${garage?.name || ''}<br/>${garage?.location || ''}</p>
  <p>Dear Sir/Madam,</p>
  <h3>SUBJECT: REQUEST FOR QUOTATION — ${extra.letterType?.toUpperCase()}</h3>
  <p>We hereby request your quotation for the following work on our vehicle:</p>
  <table>
    <tr><td><strong>Vehicle Reg. No.</strong></td><td>${repair.vehicle?.reg_number || '—'}</td></tr>
    <tr><td><strong>Make / Model</strong></td><td>${repair.vehicle?.brand || ''} ${repair.vehicle?.model || ''}</td></tr>
    <tr><td><strong>Description</strong></td><td>${repair.title}</td></tr>
    ${extra.repairItems?.length ? `<tr><td><strong>Repair Items</strong></td><td>${extra.repairItems.join(', ')}</td></tr>` : ''}
    ${extra.serviceItems?.length ? `<tr><td><strong>Service Items</strong></td><td><ul style="margin:0;padding-left:16px;">${extra.serviceItems.map(s=>`<li>${s}</li>`).join('')}</ul></td></tr>` : ''}
    ${extra.tyreSize  ? `<tr><td><strong>Tyre Size</strong></td><td>${extra.tyreSize}</td></tr>` : ''}
    ${extra.batSize   ? `<tr><td><strong>Battery Size</strong></td><td>${extra.batSize}</td></tr>` : ''}
    ${extra.driverName ? `<tr><td><strong>Driver</strong></td><td>${extra.driverName}</td></tr>` : ''}
    ${extra.mileage   ? `<tr><td><strong>Current Mileage</strong></td><td>${extra.mileage} km</td></tr>` : ''}
  </table>
  <p>Please submit your quotation including labour charges, parts cost, and estimated completion time by <strong>${deadline || '[SUBMISSION DATE]'}</strong>.</p>
  <div class="sign">
    <p>Yours faithfully,</p>
    <p style="margin-top:50px;">.......................................<br/><strong>${branch?.name || ''}</strong><br/>Transport Division</p>
  </div>`;

const buildAwardHTML = (repair, garage, branch, date, amount, extra) => `
  ${letterHeader(branch)}
  <p>Date: ${date} &nbsp;&nbsp;&nbsp; Ref: AWD-${repair.id}-${new Date().getFullYear()}</p>
  <p><strong>To,</strong><br/>${garage?.name || ''}<br/>${garage?.location || ''}</p>
  <p>Dear Sir/Madam,</p>
  <h3>SUBJECT: AWARD OF CONTRACT — ${extra.letterType?.toUpperCase()}</h3>
  <p>With reference to your quotation${extra.quotationNo ? ` No. <strong>${extra.quotationNo}</strong>` : ''}${extra.quotationDate ? ` dated <strong>${extra.quotationDate}</strong>` : ''}, we are pleased to award the contract for the following work:</p>
  ${(extra.quotationNo || extra.quotationDate || extra.quotedAmount) ? `
  <table style="margin-bottom:10px;background:#f7f7f7;">
    <tr><td colspan="2"><strong>APPROVED QUOTATION DETAILS</strong></td></tr>
    <tr><td><strong>Supplier Name</strong></td><td>${garage?.name || ''}</td></tr>
    ${extra.quotationNo   ? `<tr><td><strong>Quotation No.</strong></td><td>${extra.quotationNo}</td></tr>` : ''}
    ${extra.quotationDate ? `<tr><td><strong>Quotation Date</strong></td><td>${extra.quotationDate}</td></tr>` : ''}
    ${extra.quotedAmount  ? `<tr><td><strong>Quoted Amount</strong></td><td>Rs. ${Number(extra.quotedAmount).toLocaleString('en-LK',{minimumFractionDigits:2})}</td></tr>` : ''}
  </table>` : ''}
  <table>
    <tr><td><strong>Vehicle Reg. No.</strong></td><td>${repair.vehicle?.reg_number || '—'}</td></tr>
    <tr><td><strong>Make / Model</strong></td><td>${repair.vehicle?.brand || ''} ${repair.vehicle?.model || ''}</td></tr>
    <tr><td><strong>Description</strong></td><td>${repair.title}</td></tr>
    ${extra.repairItems?.length ? `<tr><td><strong>Repair Items</strong></td><td>${extra.repairItems.join(', ')}</td></tr>` : ''}
    ${extra.serviceItems?.length ? `<tr><td><strong>Service Items</strong></td><td><ul style="margin:0;padding-left:16px;">${extra.serviceItems.map(s=>`<li>${s}</li>`).join('')}</ul></td></tr>` : ''}
    ${extra.tyreSize  ? `<tr><td><strong>Tyre Size</strong></td><td>${extra.tyreSize}</td></tr>` : ''}
    ${extra.batSize   ? `<tr><td><strong>Battery Size</strong></td><td>${extra.batSize}</td></tr>` : ''}
    ${extra.driverName ? `<tr><td><strong>Driver</strong></td><td>${extra.driverName}</td></tr>` : ''}
    ${extra.mileage   ? `<tr><td><strong>Current Mileage</strong></td><td>${extra.mileage} km</td></tr>` : ''}
    <tr><td><strong>Awarded Amount</strong></td><td><strong>Rs. ${amount ? Number(amount).toLocaleString('en-LK',{minimumFractionDigits:2}) : '—'}</strong></td></tr>
  </table>
  <p><strong>Terms &amp; Conditions:</strong></p>
  <ol>
    <li>Work to commence within 3 working days.</li>
    <li>All original parts must be used unless agreed otherwise.</li>
    <li>Warranty of 3 months on all parts and labour.</li>
    <li>Invoice to be submitted upon completion.</li>
    <li>Payment processed within 14 working days of invoice.</li>
  </ol>
  <div class="sign">
    <p>Yours faithfully,</p>
    <p style="margin-top:50px;">.......................................<br/><strong>${branch?.name || ''}</strong><br/>Transport Division</p>
    <p style="margin-top:30px;border-top:1px solid #999;padding-top:10px;"><strong>ACKNOWLEDGEMENT:</strong><br/>
    We, ${garage?.name || ''}, acknowledge receipt of this award letter and agree to the terms.<br/>
    Signature: ................................ &nbsp;&nbsp; Date: .........................</p>
  </div>`;

// ── Main Component ────────────────────────────────────────────────────────────
export default function Repairs() {
  const { can } = useAuth();
  const [repairs,    setRepairs]    = useState([]);
  const [vehicles,   setVehicles]   = useState([]);
  const [garages,    setGarages]    = useState([]);
  const [drivers,    setDrivers]    = useState([]);
  const [branches,   setBranches]   = useState([]);
  const [tyreSizes,  setTyreSizes]  = useState([]);
  const [batSizes,   setBatSizes]   = useState([]);
  const [repTypes,   setRepTypes]   = useState([]);
  const [svcAreas,   setSvcAreas]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [filterStatus,  setFilterStatus]  = useState('');
  const [filterUrgency, setFilterUrgency] = useState('');

  // Modals
  const [modal,        setModal]        = useState(false);
  const [approveModal, setApproveModal] = useState(false);
  const [viewModal,    setViewModal]    = useState(false);
  const [letterModal,  setLetterModal]  = useState(false);
  const [selected,     setSelected]     = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [editModal,    setEditModal]    = useState(false);
  const [editForm,     setEditForm]     = useState({});

  // New request form
  const [form, setForm] = useState({
    vehicle_id:'', driver_id:'', current_mileage:'',
    title:'', description:'', urgency:'medium',
    repair_type:'mechanical', estimated_cost:'',
    repair_items:[], service_items:[],
    tyre_size:'', tyre_qty:'4', bat_size:'', bat_qty:'1',
    garage_ids:[],
  });

  // Approve form
  const [approveForm, setApproveForm] = useState({
    action:'approved', comments:'', garage_id:'', estimated_cost:'',
  });

  // Letter form — handles all 4 letter types
  const [letterForm, setLetterForm] = useState({
    letter_type: 'repair',       // repair | service | battery | tyre
    mode: 'quotation',           // quotation | award | direct_award
    branch_id: '',
    garage_ids: [],              // for quotation (multiple)
    garage_id: '',               // for award (single)
    amount: '',
    quotation_no: '', quotation_date: '', quoted_amount: '',
    date: new Date().toISOString().split('T')[0],
    // Repair
    selected_repairs: [],
    // Service
    selected_services: [],
    // Tyre
    tyre_size: '',
    tyre_qty: '4',
    // Battery
    bat_size: '',
    bat_qty: '1',
  });

  const flash = (msg, isErr=false) => {
    isErr ? setError(msg) : setSuccess(msg);
    setTimeout(() => isErr ? setError('') : setSuccess(''), 3500);
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      repairAPI.getAll({ status: filterStatus, urgency: filterUrgency, limit: 100 }),
      vehicleAPI.getAll({ limit: 100 }),
      garageAPI.getAll(),
      driverAPI.getAll({ limit: 100 }),
      api.get('/branches').catch(() => ({ data: { data: [] } })),
      referenceAPI.getTyreSizes().catch(() => ({ data: { data: [] } })),
      referenceAPI.getBatterySizes().catch(() => ({ data: { data: [] } })),
      referenceAPI.getRepairTypes().catch(() => ({ data: { data: [] } })),
      referenceAPI.getServiceAreas().catch(() => ({ data: { data: [] } })),
    ]).then(([r, v, g, d, b, ts, bs, rt, sa]) => {
      setRepairs(r.data.data);
      setVehicles(v.data.data);
      setGarages(g.data.data);
      setDrivers(d.data.data);
      setBranches(b.data.data || []);
      setTyreSizes(ts.data.data || []);
      setBatSizes(bs.data.data || []);
      setRepTypes(rt.data.data || []);
      setSvcAreas(sa.data.data || []);
    }).catch(() => flash('Failed to load', true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filterStatus, filterUrgency]);

  // ── Create repair ────────────────────────────────────────────────────────────
  const save = async e => {
    e.preventDefault(); setSaving(true);
    try {
      // Build enriched description with selected items
      let desc = form.description;
      if (form.repair_items?.length) desc += `\n\nRepair items: ${form.repair_items.join(', ')}`;
      if (form.service_items?.length) desc += `\n\nService items:\n${form.service_items.map(s => `- ${s}`).join('\n')}`;
      if (form.tyre_size)  desc += `\n\nTyre size: ${form.tyre_size} × ${form.tyre_qty} nos`;
      if (form.bat_size)   desc += `\n\nBattery size: ${form.bat_size} × ${form.bat_qty} nos`;
      if (form.current_mileage) desc += `\n\nCurrent mileage: ${form.current_mileage} km`;

      // Find driver name for storage
      const driverObj = drivers.find(d => d.id == form.driver_id);
      const payload = {
        vehicle_id:      form.vehicle_id,
        title:           form.title,
        description:     desc,
        urgency:         form.urgency,
        repair_type:     form.repair_type,
        estimated_cost:  form.estimated_cost,
        repair_items:    JSON.stringify(form.repair_items || []),
        service_items:   JSON.stringify(form.service_items || []),
        tyre_size:       form.tyre_size || null,
        tyre_qty:        form.tyre_qty || 1,
        bat_size:        form.bat_size || null,
        bat_qty:         form.bat_qty || 1,
        current_mileage: form.current_mileage || null,
        driver_name:     driverObj?.user?.name || null,
        garage_ids:      JSON.stringify(form.garage_ids || []),
        garage_id:       form.garage_ids?.[0] || null,
      };
      await repairAPI.create(payload);
      setModal(false);
      setForm({ vehicle_id:'', driver_id:'', current_mileage:'', title:'', description:'', urgency:'medium',
        repair_type:'mechanical', estimated_cost:'', repair_items:[], service_items:[],
        tyre_size:'', tyre_qty:'4', bat_size:'', bat_qty:'1', garage_ids:[] });
      load();
      flash('Repair request submitted');
    } catch (err) { flash(err.response?.data?.message || 'Failed', true); }
    finally { setSaving(false); }
  };

  // ── Approve ──────────────────────────────────────────────────────────────────
  const openApprove = r => {
    setSelected(r);
    setApproveForm({ action:'approved', comments:'', garage_id:'', estimated_cost:'' });
    setApproveModal(true);
  };
  const submitApproval = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await repairAPI.approve(selected.id, approveForm);
      setApproveModal(false); load(); flash('Review saved');
    } catch (err) { flash(err.response?.data?.message || 'Failed', true); }
    finally { setSaving(false); }
  };

  // ── Complete ─────────────────────────────────────────────────────────────────
  const complete = async r => {
    const cost = window.prompt('Enter actual cost (Rs.):');
    if (cost === null) return;
    try { await repairAPI.complete(r.id, { actual_cost: cost }); load(); flash('Repair completed'); }
    catch { flash('Failed to complete', true); }
  };

  // ── Open letter modal ─────────────────────────────────────────────────────────
  // ── Edit repair ──────────────────────────────────────────────────────────────────
  const openEdit = r => {
    const safeJSON = (v, def=[]) => { try { return JSON.parse(v) || def; } catch { return def; } };
    setEditForm({
      id: r.id, vehicle_id: r.vehicle_id,
      driver_name: r.driver_name || '', current_mileage: r.current_mileage || '',
      title: r.title, description: r.description, urgency: r.urgency,
      repair_type: r.repair_type, estimated_cost: r.estimated_cost || '',
      actual_cost: r.actual_cost || '', notes: r.notes || '', status: r.status,
      repair_items:  safeJSON(r.repair_items),
      service_items: safeJSON(r.service_items),
      tyre_size: r.tyre_size || '', tyre_qty: r.tyre_qty || 1,
      bat_size:  r.bat_size  || '', bat_qty:  r.bat_qty  || 1,
      garage_ids: safeJSON(r.garage_ids),
    });
    setEditModal(true);
  };

  const saveEdit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await repairAPI.update(editForm.id, {
        ...editForm,
        repair_items:  JSON.stringify(editForm.repair_items  || []),
        service_items: JSON.stringify(editForm.service_items || []),
        garage_ids:    JSON.stringify(editForm.garage_ids    || []),
      });
      setEditModal(false); load();
      flash('Repair updated');
    } catch (err) { flash(err.response?.data?.message || 'Update failed', true); }
    finally { setSaving(false); }
  };

  const openLetters = r => {
    setSelected(r);
    const defBranch = branches.find(b => b.is_default)?.id || branches[0]?.id || '';
    const safeJSON = (v, def=[]) => { try { return JSON.parse(v) || def; } catch { return def; } };
    // Pre-fill letter form from saved repair data
    const savedGarageIds = safeJSON(r.garage_ids);
    const repairType = ['service','battery','tyre'].includes(r.repair_type) ? r.repair_type : 'repair';
    setLetterForm({
      letter_type:        repairType,
      mode:               savedGarageIds.length > 0 ? 'quotation' : 'award',
      branch_id:          String(defBranch),
      garage_ids:         savedGarageIds,
      garage_id:          r.garage_id ? String(r.garage_id) : '',
      amount:             r.estimated_cost || '',
      date:               new Date().toISOString().split('T')[0],
      quotation_deadline: '',
      selected_repairs:   safeJSON(r.repair_items),
      selected_services:  safeJSON(r.service_items),
      tyre_size:          r.tyre_size || '',
      tyre_qty:           r.tyre_qty || '4',
      bat_size:           r.bat_size || '',
      bat_qty:            r.bat_qty || '1',
    });
    setLetterModal(true);
  };

  // ── Generate letter ───────────────────────────────────────────────────────────
  const generateLetter = () => {
    const branch = branches.find(b => b.id == letterForm.branch_id);
    const safeJSON = (v, def=[]) => { try { return JSON.parse(v) || def; } catch { return def; } };

    // Use checked items from modal; fall back to saved DB data if empty
    const repairItems   = letterForm.selected_repairs?.length
      ? letterForm.selected_repairs
      : safeJSON(selected?.repair_items);
    const serviceItems  = letterForm.selected_services?.length
      ? letterForm.selected_services
      : safeJSON(selected?.service_items);
    const tyreSize      = letterForm.tyre_size || selected?.tyre_size || '';
    const tyreQty       = letterForm.tyre_qty  || selected?.tyre_qty  || 4;
    const batSize       = letterForm.bat_size  || selected?.bat_size  || '';
    const batQty        = letterForm.bat_qty   || selected?.bat_qty   || 1;

    const extra = {
      letterType:   LETTER_TYPES.find(t => t.key === letterForm.letter_type)?.label || letterForm.letter_type,
      repairItems,
      serviceItems,
      tyreSize:     tyreSize ? `${tyreSize} × ${tyreQty} nos` : '',
      batSize:      batSize  ? `${batSize}  × ${batQty}  nos` : '',
      driverName:   selected?.driver_name    || '',
      mileage:      selected?.current_mileage || '',
    };

    if (letterForm.mode === 'quotation') {
      const targetGarages = garages.filter(g => letterForm.garage_ids.includes(g.id));
      if (!targetGarages.length) return flash('Select at least one garage', true);
      // Build all letter HTMLs and print in ONE call (one page per garage)
      const deadline = letterForm.quotation_deadline || '[SUBMISSION DATE]';
      const letters = targetGarages.map(garage =>
        buildQuotationHTML(selected, garage, branch, letterForm.date, extra, deadline)
      );
      printContent(letters);
      flash(`${targetGarages.length} quotation letter(s) sent to printer`);
    } else {
      const garage = garages.find(g => g.id == letterForm.garage_id);
      if (!garage) return flash('Select a garage', true);
      const awardExtra = { ...extra,
        quotationNo: letterForm.quotation_no,
        quotationDate: letterForm.quotation_date,
        quotedAmount: letterForm.quoted_amount };
      // Save approved quotation details on the job for future reference
      repairAPI.update(selected.id, {
        garage_id: letterForm.garage_id,
        quotation_no: letterForm.quotation_no || null,
        quotation_date: letterForm.quotation_date || null,
        quoted_amount: letterForm.quoted_amount || null,
      }).then(load).catch(() => {});
      printContent([buildAwardHTML(selected, garage, branch, letterForm.date, letterForm.amount, awardExtra)]);
      flash('Award letter sent to printer');
    }
    setLetterModal(false);
  };

  const toggleGarage = (id) => setLetterForm(f => ({
    ...f,
    garage_ids: f.garage_ids.includes(id) ? f.garage_ids.filter(x => x !== id) : [...f.garage_ids, id],
  }));

  const toggleRepair = (name) => setLetterForm(f => ({
    ...f,
    selected_repairs: f.selected_repairs.includes(name) ? f.selected_repairs.filter(x => x !== name) : [...f.selected_repairs, name],
  }));

  const toggleService = (name) => setLetterForm(f => ({
    ...f,
    selected_services: f.selected_services.includes(name) ? f.selected_services.filter(x => x !== name) : [...f.selected_services, name],
  }));

  const LETTER_TYPES = [
    { key:'repair',   label:'Repair Work',        icon:'🔧' },
    { key:'service',  label:'Service / Maintenance', icon:'⚙️' },
    { key:'battery',  label:'Battery Replacement', icon:'🔋' },
    { key:'tyre',     label:'Tyre Replacement',   icon:'🔵' },
  ];

  return (
    <div>
      <PageHeader
        title="Repair Requests"
        sub={`${repairs.length} requests`}
        action={<Button onClick={() => setModal(true)}>+ New Request</Button>}
      />
      {error   && <div className="mb-4"><Alert type="error"   message={error}   /></div>}
      {success && <div className="mb-4"><Alert type="success" message={success} /></div>}

      <div className="flex flex-wrap gap-3 mb-5">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
          <option value="">All statuses</option>
          {['pending','approved','in_progress','completed','rejected'].map(s =>
            <option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
        <select value={filterUrgency} onChange={e => setFilterUrgency(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
          <option value="">All urgencies</option>
          {URGENCIES.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>

      {loading ? <Loading /> : (
        <Table headers={['Vehicle','Title','Type','Urgency','Status','Requested','Cost (Rs.)','Actions']}>
          {repairs.map(r => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-mono text-primary-600 text-xs font-semibold">{r.vehicle?.reg_number}</td>
              <td className="px-4 py-3">
                <p className="font-medium text-sm">{r.title}</p>
                <p className="text-xs text-gray-400">{r.requester?.name}</p>
              </td>
              <td className="px-4 py-3 text-sm capitalize">{r.repair_type}</td>
              <td className={`px-4 py-3 text-sm capitalize ${urgencyColor[r.urgency]}`}>{r.urgency}</td>
              <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3 text-xs text-gray-500">{r.requested_date}</td>
              <td className="px-4 py-3 text-sm">
                {r.actual_cost ? formatRs(r.actual_cost) : r.estimated_cost ? `~${formatRs(r.estimated_cost)}` : '—'}
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-1 flex-wrap">
                  <Button variant="ghost" size="sm" title="View details"
                    onClick={() => { setSelected(r); setViewModal(true); }}>👁️</Button>
                  {can('admin','foreman') && ['pending','revision_requested'].includes(r.status) && (
                    <span className="text-xs text-primary-600 self-center" title="Review this in the Approvals menu">→ Approvals</span>
                  )}
                  {r.status === 'pending' && (
                    <span className="text-xs text-gray-400 self-center" title="Waiting for foreman review">⏳ Foreman</span>
                  )}
                  {r.manager_status === 'pending' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 self-center"
                      title="Foreman approved — waiting for manager sign-off">👔 Manager pending</span>
                  )}
                  {r.manager_status === 'approved' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 self-center"
                      title="Manager approved — award letter available">👔 Manager ✓</span>
                  )}
                  {r.manager_status === 'rejected' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 self-center"
                      title={r.manager_remark || 'Manager rejected'}>👔 Manager ✖</span>
                  )}
                  {can('admin','manager') && r.manager_status === 'pending' && (
                    <span className="text-xs text-primary-600 self-center" title="Sign off in the Approvals menu">→ Approvals</span>
                  )}

                  {can('admin','transport_officer') && ['pending','rejected','revision_requested'].includes(r.status) && (
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)}
                      title={r.status === 'rejected' ? 'Edit & re-submit for approval' : 'Edit'}>✏️</Button>
                  )}
                  {can('admin','transport_officer') && ['pending','rejected','revision_requested'].includes(r.status) && (
                    <Button variant="ghost" size="sm" title="Delete"
                      onClick={async () => {
                        if (!window.confirm(`Delete repair request "${r.title}"?`)) return;
                        try { await repairAPI.remove(r.id); load(); flash('Repair request deleted'); }
                        catch (err) { setError(err.response?.data?.message || 'Delete failed'); }
                      }}>🗑️</Button>
                  )}
                  {can('admin','transport_officer') && ['approved','in_progress'].includes(r.status) && (
                    <Button variant="ghost" size="sm" onClick={() => openLetters(r)} title="Generate Letter">📄 Letter</Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {/* ══ VIEW DETAILS (read-only, all roles) ═════════════════════════════ */}
      <Modal open={viewModal} onClose={() => setViewModal(false)}
        title={`📋 ${selected?.title || 'Repair Request'}`} size="xl">
        {selected && <RepairDetailsCard repair={selected} vehicle={vehicles.find(v => v.id == selected?.vehicle_id)} />}
        <div className="flex justify-end mt-4">
          <Button variant="ghost" onClick={() => setViewModal(false)}>Close</Button>
        </div>
      </Modal>

      {/* ══ NEW REQUEST ══════════════════════════════════════════════════════ */}
      <Modal open={modal} onClose={() => setModal(false)} title="New Repair Request" size="xl">
        <form onSubmit={save} className="space-y-4">

          {/* Vehicle + Driver + Mileage */}
          <div className="grid grid-cols-2 gap-4">
            <Select label="Vehicle *" value={form.vehicle_id}
              onChange={e => {
                const v = vehicles.find(x => String(x.id) === e.target.value);
                setForm(f => ({ ...f, vehicle_id: e.target.value,
                  tyre_size: v?.tyre_size || f.tyre_size,
                  bat_size:  v?.battery_size || f.bat_size,
                  bat_qty:   v?.battery_count ? String(v.battery_count) : f.bat_qty,
                }));
              }} required>
              <option value="">Select vehicle…</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.reg_number} — {v.brand} {v.model}</option>)}
            </Select>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Driver</label>
              <select value={form.driver_id || ''}
                onChange={e => setForm({...form, driver_id: e.target.value})}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
                <option value="">Select driver…</option>
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>{d.user?.name} ({d.license_number})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label="Current Mileage (km)" type="number"
              value={form.current_mileage || ''}
              onChange={e => setForm({...form, current_mileage: e.target.value})}
              placeholder="e.g. 45680" />
            <Select label="Urgency" value={form.urgency}
              onChange={e => setForm({...form, urgency: e.target.value})}>
              {URGENCIES.map(u => <option key={u} value={u} className="capitalize">{u}</option>)}
            </Select>
          </div>

          {/* Repair Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Repair Category *</label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { key:'mechanical', label:'Mechanical Repair', icon:'🔧' },
                { key:'service',    label:'Service',           icon:'⚙️' },
                { key:'battery',    label:'Battery',           icon:'🔋' },
                { key:'tyre',       label:'Tyre',              icon:'🔵' },
              ].map(t => (
                <button type="button" key={t.key}
                  onClick={() => setForm(f => ({...f, repair_type: t.key, repair_items:[], service_items:[], tyre_size:'', bat_size:''}))}
                  className={`p-3 rounded-xl border-2 text-center transition-all
                    ${form.repair_type === t.key ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <div className="text-xl mb-1">{t.icon}</div>
                  <div className={`text-xs font-medium ${form.repair_type === t.key ? 'text-primary-700' : 'text-gray-600'}`}>{t.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Dynamic sub-selection based on category */}
          {form.repair_type === 'mechanical' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Select Repair Items (multiple)</p>
              <div className="grid grid-cols-2 gap-2 max-h-36 overflow-y-auto">
                {repTypes.map(r => (
                  <label key={r.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm
                    ${(form.repair_items||[]).includes(r.repair_name) ? 'bg-red-100 border-red-400' : 'bg-white border-gray-200 hover:border-red-300'}`}>
                    <input type="checkbox"
                      checked={(form.repair_items||[]).includes(r.repair_name)}
                      onChange={() => setForm(f => ({...f, repair_items: (f.repair_items||[]).includes(r.repair_name)
                        ? (f.repair_items||[]).filter(x => x !== r.repair_name)
                        : [...(f.repair_items||[]), r.repair_name]}))}
                      className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{r.repair_name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {form.repair_type === 'service' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Select Service Items (multiple)</p>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {svcAreas.map(s => (
                  <label key={s.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm
                    ${(form.service_items||[]).includes(s.service_area) ? 'bg-green-100 border-green-400' : 'bg-white border-gray-200 hover:border-green-300'}`}>
                    <input type="checkbox"
                      checked={(form.service_items||[]).includes(s.service_area)}
                      onChange={() => setForm(f => ({...f, service_items: (f.service_items||[]).includes(s.service_area)
                        ? (f.service_items||[]).filter(x => x !== s.service_area)
                        : [...(f.service_items||[]), s.service_area]}))}
                      className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{s.service_area}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {form.repair_type === 'tyre' && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">Tyre Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tyre Size *</label>
                  <input list="tyre-size-list" value={form.tyre_size || ''}
                    onChange={e => setForm(f => ({...f, tyre_size: e.target.value}))}
                    placeholder={tyreSizes.length ? 'Select or type…' : 'Type tyre size (e.g. 265/65R17)'}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300" />
                  <datalist id="tyre-size-list">
                    {tyreSizes.map(t => <option key={t.id} value={t.tyre_size} />)}
                  </datalist>
                  {(() => {
                    const v = vehicles.find(x => String(x.id) === String(form.vehicle_id));
                    return v?.tyre_size
                      ? <p className="text-xs text-green-600 mt-1">from vehicle: {v.tyre_size}</p>
                      : <p className="text-xs text-gray-400 mt-1">
                          {tyreSizes.length ? `${tyreSizes.length} sizes available` : 'Type the tyre size'}
                          {form.vehicle_id ? ' · no size saved on this vehicle' : ''}
                        </p>;
                  })()}
                </div>
                <Input label="Quantity (nos)" type="number" value={form.tyre_qty || '4'}
                  onChange={e => setForm(f => ({...f, tyre_qty: e.target.value}))} />
              </div>
            </div>
          )}

          {form.repair_type === 'battery' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-3">Battery Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Battery Size *</label>
                  <input list="bat-size-list" value={form.bat_size || ''}
                    onChange={e => setForm(f => ({...f, bat_size: e.target.value}))}
                    placeholder={batSizes.length ? 'Select or type…' : 'Type battery size (e.g. 12V70A)'}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300" />
                  <datalist id="bat-size-list">
                    {batSizes.map(b => <option key={b.id} value={b.bat_size} />)}
                  </datalist>
                  {(() => {
                    const v = vehicles.find(x => String(x.id) === String(form.vehicle_id));
                    return v?.battery_size
                      ? <p className="text-xs text-green-600 mt-1">from vehicle: {v.battery_size}{v.battery_count ? ` × ${v.battery_count}` : ''}</p>
                      : <p className="text-xs text-gray-400 mt-1">
                          {batSizes.length ? `${batSizes.length} sizes available` : 'Type the battery size'}
                          {form.vehicle_id ? ' · no size saved on this vehicle' : ''}
                        </p>;
                  })()}
                </div>
                <Input label="Quantity (nos)" type="number" value={form.bat_qty || '1'}
                  onChange={e => setForm(f => ({...f, bat_qty: e.target.value}))} />
              </div>
            </div>
          )}

          {/* Title + Description */}
          <Input label="Title *" value={form.title}
            onChange={e => setForm({...form, title: e.target.value})} required
            placeholder="Brief description of issue" />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} required
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-primary-300 h-20 resize-none"
              placeholder="Detailed description…" />
          </div>

          {/* Garage multi-select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Garages to send quotation (select multiple)
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-xl p-3 bg-gray-50">
              {garages.map(g => (
                <label key={g.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm
                  ${(form.garage_ids||[]).includes(g.id) ? 'bg-primary-50 border-primary-400' : 'bg-white border-gray-200 hover:border-primary-300'}`}>
                  <input type="checkbox"
                    checked={(form.garage_ids||[]).includes(g.id)}
                    onChange={() => setForm(f => ({...f, garage_ids: (f.garage_ids||[]).includes(g.id)
                      ? (f.garage_ids||[]).filter(x => x !== g.id)
                      : [...(f.garage_ids||[]), g.id]}))}
                    className="w-3.5 h-3.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-700 leading-tight">{g.name}</p>
                    <p className="text-xs text-gray-400">{g.type}</p>
                  </div>
                </label>
              ))}
            </div>
            {(form.garage_ids||[]).length > 0 && (
              <p className="text-xs text-primary-600 font-medium mt-1">
                ✓ {(form.garage_ids||[]).length} garage(s) selected
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label="Estimated Cost (Rs.)" type="number" value={form.estimated_cost}
              onChange={e => setForm({...form, estimated_cost: e.target.value})} placeholder="e.g. 15000" />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Submitting…' : 'Submit Request'}</Button>
          </div>
        </form>
      </Modal>

      {/* ══ LETTER GENERATOR MODAL ══════════════════════════════════════════ */}
      <Modal open={letterModal} onClose={() => setLetterModal(false)}
        title={`📄 Generate Letter — ${selected?.vehicle?.reg_number}`} size="xl">
        <div className="space-y-5">

          {/* Step 1: Letter Type */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Step 1 — Letter Type</p>
            <div className="grid grid-cols-4 gap-2">
              {LETTER_TYPES.map(t => {
                // The letter must match the job's repair category
                const jobType = selected?.repair_type === 'preventive' ? 'service' : selected?.repair_type;
                const locked = jobType && t.key !== jobType;
                return (
                <button key={t.key} disabled={locked}
                  onClick={() => !locked && setLetterForm(f => ({...f, letter_type: t.key}))}
                  title={locked ? `This job is a ${jobType} job` : ''}
                  className={`p-3 rounded-xl border-2 text-center transition-all
                    ${locked ? 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed'
                      : letterForm.letter_type === t.key ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <div className="text-2xl mb-1">{t.icon}</div>
                  <div className={`text-xs font-medium ${letterForm.letter_type === t.key && !locked ? 'text-primary-700' : 'text-gray-600'}`}>{t.label}</div>
                </button>
              ); })}
            </div>
          </div>

          {/* Step 2: Mode */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Step 2 — Letter Mode</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key:'quotation',    label:'📨 Quotation Request', sub:'Send to multiple garages' },
                { key:'award',        label:'🏆 Award Letter',       sub:'Award after quotation' },
                { key:'direct_award', label:'⚡ Direct Award',      sub:'Award without quotation' },
              ].map(m => {
                // Award letters require the manager's sign-off
                const needsManager = m.key !== 'quotation';
                const blocked = needsManager && selected?.manager_status !== 'approved';
                return (
                <button key={m.key} disabled={blocked}
                  onClick={() => !blocked && setLetterForm(f => ({...f, mode: m.key}))}
                  title={blocked ? 'Available after manager approval' : ''}
                  className={`p-3 rounded-xl border-2 text-left transition-all
                    ${blocked ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                      : letterForm.mode === m.key ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <div className={`text-sm font-medium ${letterForm.mode === m.key && !blocked ? 'text-primary-700' : 'text-gray-700'}`}>{m.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{blocked ? '🔒 needs manager approval' : m.sub}</div>
                </button>
              ); })}
            </div>
          </div>

          {/* Step 3: Letter-type specific details */}
          {letterForm.letter_type === 'repair' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-3">🔧 Select Repair Items (multiple)</p>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {repTypes.map(r => (
                  <label key={r.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm
                    ${letterForm.selected_repairs.includes(r.repair_name) ? 'bg-red-100 border-red-400' : 'bg-white border-gray-200'}`}>
                    <input type="checkbox" checked={letterForm.selected_repairs.includes(r.repair_name)}
                      onChange={() => toggleRepair(r.repair_name)} className="w-3.5 h-3.5" />
                    {r.repair_name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {letterForm.letter_type === 'service' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-3">⚙️ Select Service Items (multiple)</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {svcAreas.map(s => (
                  <label key={s.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm
                    ${letterForm.selected_services.includes(s.service_area) ? 'bg-green-100 border-green-400' : 'bg-white border-gray-200'}`}>
                    <input type="checkbox" checked={letterForm.selected_services.includes(s.service_area)}
                      onChange={() => toggleService(s.service_area)} className="w-3.5 h-3.5" />
                    {s.service_area}
                  </label>
                ))}
              </div>
            </div>
          )}

          {letterForm.letter_type === 'tyre' && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">🔵 Tyre Replacement Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tyre Size *</label>
                  <select value={letterForm.tyre_size} onChange={e => setLetterForm(f => ({...f, tyre_size: e.target.value}))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
                    <option value="">Select tyre size…</option>
                    {tyreSizes.map(t => <option key={t.id} value={t.tyre_size}>{t.tyre_size}</option>)}
                  </select>
                </div>
                <Input label="Quantity (nos)" type="number" value={letterForm.tyre_qty}
                  onChange={e => setLetterForm(f => ({...f, tyre_qty: e.target.value}))} />
              </div>
            </div>
          )}

          {letterForm.letter_type === 'battery' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-3">🔋 Battery Replacement Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Battery Size *</label>
                  <select value={letterForm.bat_size} onChange={e => setLetterForm(f => ({...f, bat_size: e.target.value}))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
                    <option value="">Select battery size…</option>
                    {batSizes.map(b => <option key={b.id} value={b.bat_size}>{b.bat_size}</option>)}
                  </select>
                </div>
                <Input label="Quantity (nos)" type="number" value={letterForm.bat_qty}
                  onChange={e => setLetterForm(f => ({...f, bat_qty: e.target.value}))} />
              </div>
            </div>
          )}

          {/* Step 4: Branch + Garage + Date */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Step 3 — Branch & Garage</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Branch</label>
                <select value={letterForm.branch_id} onChange={e => setLetterForm(f => ({...f, branch_id: e.target.value}))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}{b.is_default ? ' ★' : ''}</option>)}
                </select>
              </div>
              <Input label="Letter Date" type="date" value={letterForm.date}
                onChange={e => setLetterForm(f => ({...f, date: e.target.value}))} />
            </div>
            {letterForm.mode === 'quotation' && (
              <Input label="Quotation Submission Deadline *"
                type="date"
                value={letterForm.quotation_deadline || ''}
                onChange={e => setLetterForm(f => ({...f, quotation_deadline: e.target.value}))} />
            )}

            {/* Award amount */}
            {(letterForm.mode === 'award' || letterForm.mode === 'direct_award') && (
              <>
                <Input label="Awarded Amount (Rs.) *" type="number" value={letterForm.amount}
                  onChange={e => setLetterForm(f => ({...f, amount: e.target.value}))} placeholder="e.g. 85000" />
                {letterForm.mode === 'award' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-3">
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">📑 Approved Quotation Details</p>
                    <div className="grid grid-cols-3 gap-3">
                      <Input label="Quotation No." value={letterForm.quotation_no}
                        onChange={e => setLetterForm(f => ({...f, quotation_no: e.target.value}))} />
                      <Input label="Quotation Date" type="date" value={letterForm.quotation_date}
                        onChange={e => setLetterForm(f => ({...f, quotation_date: e.target.value}))} />
                      <Input label="Quoted Amount (Rs.)" type="number" step="0.01" value={letterForm.quoted_amount}
                        onChange={e => setLetterForm(f => ({...f, quoted_amount: e.target.value}))} />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Supplier name is taken from the awarded garage below. These details print on the award letter and are saved to the job.</p>
                  </div>
                )}
              </>
            )}

            {/* Garage selection */}
            {letterForm.mode === 'quotation' ? (
              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">Select garages (one letter per garage):</p>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {[...garages].sort((a, b) => {
                    const k = LETTER_SPEC[letterForm.letter_type];
                    return (gSpecs(b).includes(k) ? 1 : 0) - (gSpecs(a).includes(k) ? 1 : 0);
                  }).map(g => {
                    const match = gSpecs(g).includes(LETTER_SPEC[letterForm.letter_type]);
                    return (
                    <label key={g.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm
                      ${letterForm.garage_ids.includes(g.id) ? 'bg-primary-50 border-primary-400' : 'bg-white border-gray-200'}`}>
                      <input type="checkbox" checked={letterForm.garage_ids.includes(g.id)}
                        onChange={() => toggleGarage(g.id)} className="w-3.5 h-3.5" />
                      <span className="flex-1">{g.name}
                        {match && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px]">✓ specialty match</span>}
                      </span>
                      <span className="text-xs text-gray-400">{g.location}</span>
                    </label>
                  ); })}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Awarded Garage *</label>
                <select value={letterForm.garage_id} onChange={e => setLetterForm(f => ({...f, garage_id: e.target.value}))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
                  <option value="">Select garage…</option>
                  {[...garages].sort((a, b) => {
                    const k = LETTER_SPEC[letterForm.letter_type];
                    return (gSpecs(b).includes(k) ? 1 : 0) - (gSpecs(a).includes(k) ? 1 : 0);
                  }).map(g => (
                    <option key={g.id} value={g.id}>
                      {gSpecs(g).includes(LETTER_SPEC[letterForm.letter_type]) ? '✓ ' : ''}{g.name} — {g.location}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setLetterModal(false)}>Cancel</Button>
            <Button onClick={generateLetter}>
              🖨 Generate & Print Letter{letterForm.mode === 'quotation' && letterForm.garage_ids.length > 1 ? `s (${letterForm.garage_ids.length})` : ''}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ══ EDIT REPAIR MODAL ═══════════════════════════════════════════════ */}
      <Modal open={editModal} onClose={() => setEditModal(false)}
        title={`✏️ Edit — ${editForm.title || 'Repair Request'}`} size="xl">
        <form onSubmit={saveEdit} className="space-y-4">

          {/* Vehicle (read-only) + Driver name + Mileage */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <p className="text-xs text-gray-400">Vehicle</p>
              <p className="font-medium text-primary-600">
                {vehicles.find(v => v.id == editForm.vehicle_id)?.reg_number || '—'}
              </p>
            </div>
            <Input label="Driver Name" value={editForm.driver_name || ''}
              onChange={e => setEditForm({...editForm, driver_name: e.target.value})}
              placeholder="Driver name" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label="Current Mileage (km)" type="number" value={editForm.current_mileage || ''}
              onChange={e => setEditForm({...editForm, current_mileage: e.target.value})} />
            <Select label="Urgency" value={editForm.urgency || 'medium'}
              onChange={e => setEditForm({...editForm, urgency: e.target.value})}>
              {URGENCIES.map(u => <option key={u} value={u}>{u}</option>)}
            </Select>
          </div>

          {/* Repair Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Repair Category</label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { key:'mechanical', label:'Mechanical', icon:'🔧' },
                { key:'service',    label:'Service',    icon:'⚙️' },
                { key:'battery',    label:'Battery',    icon:'🔋' },
                { key:'tyre',       label:'Tyre',       icon:'🔵' },
              ].map(t => (
                <button type="button" key={t.key}
                  onClick={() => setEditForm(f => ({...f, repair_type: t.key}))}
                  className={`p-3 rounded-xl border-2 text-center transition-all
                    ${editForm.repair_type === t.key ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <div className="text-xl mb-1">{t.icon}</div>
                  <div className={`text-xs font-medium ${editForm.repair_type === t.key ? 'text-primary-700' : 'text-gray-600'}`}>{t.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Sub-selection */}
          {editForm.repair_type === 'mechanical' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Repair Items</p>
              <div className="grid grid-cols-2 gap-2 max-h-36 overflow-y-auto">
                {repTypes.map(r => (
                  <label key={r.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm
                    ${(editForm.repair_items||[]).includes(r.repair_name) ? 'bg-red-100 border-red-400' : 'bg-white border-gray-200'}`}>
                    <input type="checkbox"
                      checked={(editForm.repair_items||[]).includes(r.repair_name)}
                      onChange={() => setEditForm(f => ({...f, repair_items: (f.repair_items||[]).includes(r.repair_name)
                        ? (f.repair_items||[]).filter(x => x !== r.repair_name)
                        : [...(f.repair_items||[]), r.repair_name]}))}
                      className="w-3.5 h-3.5 flex-shrink-0" />
                    {r.repair_name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {editForm.repair_type === 'service' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Service Items</p>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {svcAreas.map(s => (
                  <label key={s.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm
                    ${(editForm.service_items||[]).includes(s.service_area) ? 'bg-green-100 border-green-400' : 'bg-white border-gray-200'}`}>
                    <input type="checkbox"
                      checked={(editForm.service_items||[]).includes(s.service_area)}
                      onChange={() => setEditForm(f => ({...f, service_items: (f.service_items||[]).includes(s.service_area)
                        ? (f.service_items||[]).filter(x => x !== s.service_area)
                        : [...(f.service_items||[]), s.service_area]}))}
                      className="w-3.5 h-3.5 flex-shrink-0" />
                    {s.service_area}
                  </label>
                ))}
              </div>
            </div>
          )}

          {editForm.repair_type === 'tyre' && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Tyre Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tyre Size</label>
                  <select value={editForm.tyre_size || ''}
                    onChange={e => setEditForm(f => ({...f, tyre_size: e.target.value}))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
                    <option value="">Select…</option>
                    {tyreSizes.map(t => <option key={t.id} value={t.tyre_size}>{t.tyre_size}</option>)}
                  </select>
                </div>
                <Input label="Qty" type="number" value={editForm.tyre_qty || '4'}
                  onChange={e => setEditForm(f => ({...f, tyre_qty: e.target.value}))} />
              </div>
            </div>
          )}

          {editForm.repair_type === 'battery' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Battery Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Battery Size</label>
                  <select value={editForm.bat_size || ''}
                    onChange={e => setEditForm(f => ({...f, bat_size: e.target.value}))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300">
                    <option value="">Select…</option>
                    {batSizes.map(b => <option key={b.id} value={b.bat_size}>{b.bat_size}</option>)}
                  </select>
                </div>
                <Input label="Qty" type="number" value={editForm.bat_qty || '1'}
                  onChange={e => setEditForm(f => ({...f, bat_qty: e.target.value}))} />
              </div>
            </div>
          )}

          {/* Title + Description */}
          <Input label="Title *" value={editForm.title || ''}
            onChange={e => setEditForm({...editForm, title: e.target.value})} required />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={editForm.description || ''} onChange={e => setEditForm({...editForm, description: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-primary-300 h-20 resize-none" />
          </div>

          {/* Costs + Status */}
          <div className="grid grid-cols-3 gap-4">
            <Input label="Estimated Cost (Rs.)" type="number" value={editForm.estimated_cost || ''}
              onChange={e => setEditForm({...editForm, estimated_cost: e.target.value})} />
            <Input label="Actual Cost (Rs.)" type="number" value={editForm.actual_cost || ''}
              onChange={e => setEditForm({...editForm, actual_cost: e.target.value})} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <div className="px-3 py-2 rounded-lg border bg-gray-50 text-sm text-gray-600 capitalize">
                {(editForm.status || 'pending').replace('_',' ')}
              </div>
              <p className="text-xs text-gray-400 mt-1">Status is controlled by the foreman's review. Saving re-submits this request for approval.</p>
            </div>
          </div>

          {/* Garages */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Garages (for quotation)</label>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-xl p-3 bg-gray-50">
              {garages.map(g => (
                <label key={g.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm
                  ${(editForm.garage_ids||[]).includes(g.id) ? 'bg-primary-50 border-primary-400' : 'bg-white border-gray-200'}`}>
                  <input type="checkbox"
                    checked={(editForm.garage_ids||[]).includes(g.id)}
                    onChange={() => setEditForm(f => ({...f, garage_ids: (f.garage_ids||[]).includes(g.id)
                      ? (f.garage_ids||[]).filter(x => x !== g.id)
                      : [...(f.garage_ids||[]), g.id]}))}
                    className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="font-medium text-gray-700">{g.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setEditModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
          </div>
        </form>
      </Modal>

    </div>
  );
}
